import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjects } from '../hooks/useProjects';
import { usePermissions } from '../hooks/usePermissions';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Container, Row, Col } from '../components/ui/Layout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Spinner } from '../components/ui/Spinner';
import { FiEdit2, FiSave, FiX, FiPlus, FiRefreshCw, FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import chatbotService from '../api/chatbotService';
import apiService from '../api/apiService';
import { useQueryClient } from '@tanstack/react-query';

const ProjectOverview = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { useProject, useUpdateProject } = useProjects();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  
  // Project data query
  const { data: projectData, isLoading: isProjectLoading, isError: isProjectError, error: projectError } = useProject(projectId);
  const project = projectData?.data?.project || {};
  
  // Get current year and month for billing report
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  
  // Fetch billing report for usage statistics
  const { data: billingReport, isLoading: isBillingLoading } = useQuery({
    queryKey: ['billingReport', projectId, currentYear, currentMonth],
    queryFn: () => chatbotService.generateBillingReport(projectId, currentYear, currentMonth),
  });

  // Fetch quota status
  const { data: quotaStatus, isLoading: isQuotaLoading, refetch: refetchQuotaStatus } = useQuery({
    queryKey: ['quotaStatus', projectId],
    queryFn: () => apiService.getQuotaStatus(projectId),
    enabled: !!projectId && !!project.messageQuota?.enabled,
    staleTime: 30000, // Cache for 30 seconds to improve performance
    refetchOnWindowFocus: false
  });
  
  // Editable fields state
  const [isEditing, setIsEditing] = useState(false);
  const [editableFields, setEditableFields] = useState({
    name: '',
    clientName: '',
    description: '',
    rateLimiting: {
      enabled: false,
      maxMessages: 20,
      timeWindow: 30 // in minutes
    },
    timeSettings: {
      useCurrentTime: false,
      timezone: 'UTC'
    },
    messageQuota: {
      enabled: false,
      totalTokens: 1000,
      refreshMonthly: true,
      thresholdAlert: {
        enabled: true,
        thresholdPercentage: 80,
        emailRecipients: []
      },
      quotaExceededMessage: ''
    }
  });

  // Quota operation states
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpReason, setTopUpReason] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [newEmailRecipient, setNewEmailRecipient] = useState('');

  // Sync to production state
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  
  // Update mutation
  const updateProjectMutation = useUpdateProject();
  
  // Initialize editable fields when project data loads
  useEffect(() => {
    if (project) {
      setEditableFields({
        name: project.name || '',
        clientName: project.clientName || '',
        description: project.description || '',
        rateLimiting: {
          enabled: project.rateLimiting?.enabled || false,
          maxMessages: project.rateLimiting?.maxMessages || 20,
          // Convert from seconds to minutes for the UI
          timeWindow: project.rateLimiting?.timeWindow ? Math.round(project.rateLimiting.timeWindow / 60) : 30
        },
        timeSettings: {
          useCurrentTime: project.timeSettings?.useCurrentTime || false,
          timezone: project.timeSettings?.timezone || 'UTC'
        },
        messageQuota: {
          enabled: project.messageQuota?.enabled || false,
          totalTokens: project.messageQuota?.totalTokens || 1000,
          refreshMonthly: project.messageQuota?.refreshMonthly || true,
          thresholdAlert: {
            enabled: project.messageQuota?.thresholdAlert?.enabled || true,
            thresholdPercentage: project.messageQuota?.thresholdAlert?.thresholdPercentage || 80,
            emailRecipients: project.messageQuota?.thresholdAlert?.emailRecipients || []
          },
          quotaExceededMessage: project.messageQuota?.quotaExceededMessage || ''
          // Note: currentTokens and lastRefreshDate are deliberately excluded as they are runtime fields
          // managed by the backend and should not be part of the editable form data
        }
      });
    }
  }, [project]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableFields({
      ...editableFields,
      [name]: value
    });
  };
  
  // Handle rate limiting input changes
  const handleRateLimitingChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditableFields({
      ...editableFields,
      rateLimiting: {
        ...editableFields.rateLimiting,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value)
      },
      timeSettings: {
        ...editableFields.timeSettings,
        timezone: name === 'timezone' ? value : editableFields.timeSettings.timezone
      }
    });
  };

  // Handle message quota input changes
  const handleMessageQuotaChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('thresholdAlert.')) {
      const alertField = name.split('.')[1];
      setEditableFields({
        ...editableFields,
        messageQuota: {
          ...editableFields.messageQuota,
          thresholdAlert: {
            ...editableFields.messageQuota.thresholdAlert,
            [alertField]: type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value)
          }
        }
      });
    } else {
      setEditableFields({
        ...editableFields,
        messageQuota: {
          ...editableFields.messageQuota,
          [name]: type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value)
        }
      });
    }
  };

  // Add email recipient for threshold alerts
  const addEmailRecipient = () => {
    if (newEmailRecipient.trim() && isValidEmail(newEmailRecipient.trim())) {
      const currentRecipients = editableFields.messageQuota.thresholdAlert.emailRecipients;
      if (!currentRecipients.includes(newEmailRecipient.trim())) {
        setEditableFields({
          ...editableFields,
          messageQuota: {
            ...editableFields.messageQuota,
            thresholdAlert: {
              ...editableFields.messageQuota.thresholdAlert,
              emailRecipients: [...currentRecipients, newEmailRecipient.trim()]
            }
          }
        });
        setNewEmailRecipient('');
      } else {
        toast.warning('Email already added');
      }
    } else {
      toast.error('Please enter a valid email address');
    }
  };

  // Remove email recipient
  const removeEmailRecipient = (emailToRemove) => {
    setEditableFields({
      ...editableFields,
      messageQuota: {
        ...editableFields.messageQuota,
        thresholdAlert: {
          ...editableFields.messageQuota.thresholdAlert,
          emailRecipients: editableFields.messageQuota.thresholdAlert.emailRecipients.filter(email => email !== emailToRemove)
        }
      }
    });
  };

  // Validate email
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle quota top-up
  const handleTopUp = async () => {
    if (!topUpAmount || isNaN(topUpAmount) || Number(topUpAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setTopUpLoading(true);
    try {
      await apiService.topUpQuota(projectId, {
        amount: Number(topUpAmount),
        reason: topUpReason || 'Manual top-up'
      });
      
      toast.success(`Successfully added ${topUpAmount} tokens`);
      setShowTopUpModal(false);
      setTopUpAmount('');
      setTopUpReason('');
      refetchQuotaStatus();
      queryClient.invalidateQueries(['project', projectId]);
    } catch (error) {
      toast.error('Failed to top up quota: ' + (error.response?.data?.message || error.message));
    } finally {
      setTopUpLoading(false);
    }
  };

  // Handle quota reset
  const handleQuotaReset = async () => {
    setResetLoading(true);
    try {
      await apiService.resetQuota(projectId);
      
      toast.success('Quota reset successfully');
      setShowResetModal(false);
      refetchQuotaStatus();
      queryClient.invalidateQueries(['project', projectId]);
    } catch (error) {
      toast.error('Failed to reset quota: ' + (error.response?.data?.message || error.message));
    } finally {
      setResetLoading(false);
    }
  };

  // Format percentage
  const formatPercentage = (percentage) => {
    return `${Math.round(percentage * 100) / 100}%`;
  };
  
  // Toggle edit mode
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
    
    if (!isEditing) {
      // When entering edit mode, reset fields to current values
      setEditableFields({
        name: project.name || '',
        clientName: project.clientName || '',
        description: project.description || '',
        rateLimiting: {
          enabled: project.rateLimiting?.enabled || false,
          maxMessages: project.rateLimiting?.maxMessages || 20,
          // Convert from seconds to minutes for the UI
          timeWindow: project.rateLimiting?.timeWindow ? Math.round(project.rateLimiting.timeWindow / 60) : 30
        },
        timeSettings: {
          useCurrentTime: project.timeSettings?.useCurrentTime || false,
          timezone: project.timeSettings?.timezone || 'UTC'
        },
        messageQuota: {
          enabled: project.messageQuota?.enabled || false,
          totalTokens: project.messageQuota?.totalTokens || 1000,
          refreshMonthly: project.messageQuota?.refreshMonthly || true,
          thresholdAlert: {
            enabled: project.messageQuota?.thresholdAlert?.enabled || true,
            thresholdPercentage: project.messageQuota?.thresholdAlert?.thresholdPercentage || 80,
            emailRecipients: project.messageQuota?.thresholdAlert?.emailRecipients || []
          },
          quotaExceededMessage: project.messageQuota?.quotaExceededMessage || ''
          // Note: currentTokens and lastRefreshDate are deliberately excluded as they are runtime fields
          // managed by the backend and should not be part of the editable form data
        }
      });
    }
  };
  
  // Save changes
  const saveChanges = async () => {
    try {
      // Convert timeWindow from minutes to seconds before saving
      const dataToSave = {
        ...editableFields,
        rateLimiting: {
          ...editableFields.rateLimiting,
          timeWindow: editableFields.rateLimiting.timeWindow * 60 // Convert to seconds
        },
        timeSettings: {
          ...editableFields.timeSettings,
          timezone: editableFields.timeSettings.timezone
        },
        messageQuota: {
          ...editableFields.messageQuota,
          thresholdAlert: {
            ...editableFields.messageQuota.thresholdAlert,
            emailRecipients: editableFields.messageQuota.thresholdAlert.emailRecipients.map(email => email.trim())
          }
        }
      };

      // Remove currentTokens from messageQuota to prevent resetting the current token count
      // currentTokens should only be managed by the backend through usage, top-ups, and resets
      if (dataToSave.messageQuota) {
        delete dataToSave.messageQuota.currentTokens;
        delete dataToSave.messageQuota.lastRefreshDate;
      }
      
      // Debug logging to see what data is being sent
      console.log('[ProjectOverview] Saving project data:', JSON.stringify(dataToSave, null, 2));
      if (dataToSave.messageQuota && dataToSave.messageQuota.thresholdAlert) {
        console.log('[ProjectOverview] EmailRecipients being sent:', dataToSave.messageQuota.thresholdAlert.emailRecipients);
        console.log('[ProjectOverview] EmailRecipients count:', dataToSave.messageQuota.thresholdAlert.emailRecipients?.length || 0);
        console.log('[ProjectOverview] EmailRecipients type:', typeof dataToSave.messageQuota.thresholdAlert.emailRecipients);
        console.log('[ProjectOverview] EmailRecipients isArray:', Array.isArray(dataToSave.messageQuota.thresholdAlert.emailRecipients));
      }
      
      await updateProjectMutation.mutateAsync({
        id: projectId,
        data: dataToSave
      });
      
      // Invalidate the project query to refresh data in other components
      queryClient.invalidateQueries(['project', projectId]);
      
      setIsEditing(false);
      toast.success('Project updated successfully');
    } catch (err) {
      console.error('Error updating project:', err);
      toast.error('Failed to update project: ' + (err.message || 'Unknown error'));
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Format number with commas
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  // Sync to production functionality
  const shouldShowSyncButton = () => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    console.log('[ProjectOverview] Environment check:', { 
      environment, 
      shouldShow: environment === 'local' || environment === 'staging' 
    });
    return environment === 'local' || environment === 'staging';
  };

  const handleSyncToProduction = () => {
    console.log('[ProjectOverview] Sync button clicked for project:', project);
    setSyncResult(null);
    setSyncError(null);
    setShowSyncModal(true);
  };

  const handleConfirmSync = async () => {
    if (!projectId) return;

    setSyncLoading(true);
    setSyncError(null);

    try {
      const response = await apiService.syncProjectToProduction(projectId);
      
      setSyncResult(response.data);
      console.log('Project synced successfully:', response.data);
      
      // Auto-close modal after 3 seconds on success
      setTimeout(() => {
        handleCancelSync();
      }, 3000);

    } catch (error) {
      console.error('Error syncing project:', error);
      setSyncError(error.response?.data?.message || 'Failed to sync project to production');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCancelSync = () => {
    setShowSyncModal(false);
    setSyncResult(null);
    setSyncError(null);
  };
  
  const isLoading = isProjectLoading || isBillingLoading || isQuotaLoading;
  
  if (isLoading) {
    return (
      <Layout>
        <Container className="py-8">
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" />
          </div>
        </Container>
      </Layout>
    );
  }
  
  if (isProjectError) {
    return (
      <Layout>
        <Container className="py-8">
          <div className="bg-red-50 text-red-700 p-4 rounded-md">
            <h3 className="text-lg font-medium">Error loading project</h3>
            <p>{projectError?.message || 'Unknown error occurred'}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/projects')}
            >
              Back to Projects
            </Button>
          </div>
        </Container>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <Container className="py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Project Overview</h1>
        </div>
        
        {/* Usage Statistics - Horizontal Layout */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Usage Statistics <label>for {monthNames[currentMonth-1]} {currentYear}</label></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex">
            {/* Chat Sessions */}
            <Card className="text-center w-100p m-lr-10px shadow">
              <CardContent className="p-6">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Total Chat Sessions</h3>
                <p className="text-3xl font-bold">{formatNumber(billingReport?.sessionCount)}</p>
                {billingReport?.fromCache && (
                  <p className="text-xs text-gray-500 mt-1">Cached data</p>
                )}
              </CardContent>
            </Card>
            
            {/* Total Messages */}
            <Card className="text-center w-100p m-lr-10px shadow">
              <CardContent className="p-6">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Total Messages</h3>
                <p className="text-3xl font-bold">{formatNumber(billingReport?.totalMessages)}</p>
                {billingReport?.fromCache && (
                  <p className="text-xs text-gray-500 mt-1">Cached data</p>
                )}
              </CardContent>
            </Card>
          </div>
          <div className="mt-4 text-right">
            <Button 
              variant="outline"
              className="btn btn-primary text-smd" 
              onClick={() => navigate(`/projects/${projectId}/billing`)}
            >
              View Detailed Analytics
            </Button>
          </div>
        </div>

        {/* Message Quota Status */}
        {project.messageQuota?.enabled && (
          <Card className="mb-6 shadow">
            <CardHeader className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Message Quota Status</h2>
              <div className="flex gap-2">
                {permissions.canEditProject(projectId) && (
                  <>
                    <Button
                      onClick={() => setShowTopUpModal(true)}
                      className="btn btn-success text-smd"
                      size="sm"
                    >
                      <FiPlus className="mr-2" /> Top Up
                    </Button>
                    <Button
                      onClick={() => setShowResetModal(true)}
                      className="btn btn-warning text-smd"
                      size="sm"
                    >
                      <FiRefreshCw className="mr-2" /> Reset
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!project?.messageQuota?.enabled ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Message quota is not enabled for this project.</p>
                  <p className="text-sm mt-2">Enable quota in the settings below to view status.</p>
                </div>
              ) : isQuotaLoading ? (
                <div className="flex justify-center items-center h-32">
                  <Spinner size="lg" />
                </div>
              ) : quotaStatus?.data && quotaStatus.data.enabled ? (
                <div className="space-y-4">
                  {/* Current Usage */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <h3 className="text-lg font-medium text-gray-700 mb-2">Current Tokens</h3>
                      <p className="text-3xl font-bold text-blue-600">
                        {formatNumber(quotaStatus.data.currentTokens)}
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <h3 className="text-lg font-medium text-gray-700 mb-2">Total Tokens</h3>
                      <p className="text-3xl font-bold text-gray-800">
                        {formatNumber(quotaStatus.data.totalTokens)}
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <h3 className="text-lg font-medium text-gray-700 mb-2">Usage</h3>
                      <p className="text-3xl font-bold text-orange-600">
                        {formatPercentage(quotaStatus.data.usagePercentage)}
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${
                        quotaStatus.data.usagePercentage >= 90 ? 'bg-red-500' :
                        quotaStatus.data.usagePercentage >= 80 ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      style={{ 
                        width: `${Math.min(quotaStatus.data.usagePercentage, 100)}%` 
                      }}
                    ></div>
                  </div>

                  {/* Alert Status */}
                  {quotaStatus.data.usagePercentage >= quotaStatus.data.thresholdAlert.thresholdPercentage && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <FiAlertTriangle className="text-yellow-500 mt-0.5 mr-3" />
                        <div>
                          <h4 className="text-yellow-800 font-medium">Quota Alert</h4>
                          <p className="text-yellow-700 text-sm">
                            Usage has exceeded {quotaStatus.data.thresholdAlert.thresholdPercentage}% threshold.
                            {quotaStatus.data.thresholdAlert.alertSent ? 
                              ' Alert emails have been sent.' : 
                              ' Alert emails will be sent when usage reaches the threshold.'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quota Settings Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Quota Settings</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Monthly Refresh:</span>
                          <span className={quotaStatus.data.refreshMonthly ? 'text-green-600' : 'text-gray-500'}>
                            {quotaStatus.data.refreshMonthly ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        {quotaStatus.data.lastRefreshDate && (
                          <div className="flex justify-between">
                            <span>Last Refresh:</span>
                            <span>{formatDate(quotaStatus.data.lastRefreshDate)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span>Alert Threshold:</span>
                          <span>{quotaStatus.data.thresholdAlert.thresholdPercentage}%</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Alert Settings</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex justify-between">
                          <span>Alerts Enabled:</span>
                          <span className={quotaStatus.data.thresholdAlert.enabled ? 'text-green-600' : 'text-gray-500'}>
                            {quotaStatus.data.thresholdAlert.enabled ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Recipients:</span>
                          <span>{quotaStatus.data.thresholdAlert.emailRecipients.length} configured</span>
                        </div>
                        {quotaStatus.data.thresholdAlert.emailRecipients.length > 0 && (
                          <div className="mt-2">
                            <div className="flex flex-wrap gap-1">
                              {quotaStatus.data.thresholdAlert.emailRecipients.map((email, index) => (
                                <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                  {email}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Recent Top-Up History */}
                  {quotaStatus.data.topUpHistory && quotaStatus.data.topUpHistory.length > 0 && (
                    <div className="pt-4 border-t">
                      <h4 className="font-medium text-gray-700 mb-2">Recent Top-Ups</h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {quotaStatus.data.topUpHistory.slice(0, 5).map((topUp, index) => (
                          <div key={index} className="flex justify-between items-center text-sm">
                            <div>
                              <span className="font-medium text-green-600">+{formatNumber(topUp.amount)}</span>
                              <span className="text-gray-500 ml-2">{topUp.reason}</span>
                            </div>
                            <span className="text-gray-400 text-xs">
                              {formatDate(topUp.addedAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>Unable to load quota status</p>
                  <Button 
                    onClick={() => refetchQuotaStatus()} 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                  >
                    Retry
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Project Details */}
        <Card className="mb-6 p-4 shadow">
          <CardHeader className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Project Details</h2>
            <div className="flex gap-2">
              {shouldShowSyncButton() && (
                <Button
                  onClick={handleSyncToProduction}
                  className="btn btn-primary text-smd"
                  size="sm"
                >
                  🚀 Sync to Production
                </Button>
              )}
              {permissions.canEditProject(projectId) && (
                <Button
                  onClick={toggleEditMode}
                  className="btn btn-primary text-smd"
                  variant={isEditing ? "secondary" : "primary"}
                  size="sm"
                >
                  {isEditing ? (
                    <>
                      <FiX className="mr-2" /> Cancel Editing
                    </>
                  ) : (
                    <>
                      <FiEdit2 className="mr-2" /> Edit Project
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-bold text-gray-700 mb-1">Project ID</p>
                <p className="font-mono bg-gray-100 p-2 rounded">{projectId}</p>
              </div>
              
              <div>
                <p className="text-sm font-bold text-gray-700 mb-1">Project Name</p>
                {isEditing && permissions.canEditProject(projectId) ? (
                  <Input
                    name="name"
                    value={editableFields.name}
                    onChange={handleInputChange}
                    placeholder="Enter project name"
                    className="w-full text-smd"
                  />
                ) : (
                  <p className="font-medium">{project.name || 'Unnamed Project'}</p>
                )}
              </div>
              
              <div>
                <p className="text-sm font-bold text-gray-700 mb-1">Client Name</p>
                {isEditing && permissions.canEditProject(projectId) ? (
                  <Input
                    name="clientName"
                    value={editableFields.clientName}
                    onChange={handleInputChange}
                    placeholder="Enter client name"
                    className="w-full text-smd"
                  />
                ) : (
                  <p>{project.clientName || 'No client specified'}</p>
                )}
              </div>
              
              <div>
                <p className="text-sm font-bold text-gray-700 mb-1">Description</p>
                {isEditing && permissions.canEditProject(projectId) ? (
                  <Textarea
                    name="description"
                    value={editableFields.description}
                    onChange={handleInputChange}
                    placeholder="Enter project description"
                    rows={4}
                    className="w-full text-smd"
                  />
                ) : (
                  <p className="whitespace-pre-line">
                    {project.description || 'No description provided'}
                  </p>
                )}
              </div>
              
              {/* Rate Limiting Settings */}
              <div>
                <p className="text-sm font-bold text-gray-700 mb-1">Message Rate Limiting</p>
                {isEditing && permissions.canEditProject(projectId) ? (
                  <div className="bg-gray-50 p-4 rounded space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="enabled"
                        id="rateLimitEnabled"
                        checked={editableFields.rateLimiting.enabled}
                        onChange={handleRateLimitingChange}
                        className="mr-2 h-4 w-4"
                      />
                      <label htmlFor="rateLimitEnabled" className="text-gray-700">
                        Enable message rate limiting
                      </label>
                    </div>
                    
                    {editableFields.rateLimiting.enabled ? (
                      <div className="flex flex-wrap gap-4 mt-2">
                        <div className="w-100px">
                          <label htmlFor="maxMessages" className="block text-sm text-gray-700 mb-1">
                            Maximum messages
                          </label>
                          <Input
                            type="number"
                            name="maxMessages"
                            id="maxMessages"
                            value={editableFields.rateLimiting.maxMessages}
                            onChange={handleRateLimitingChange}
                            min="1"
                            max="1000"
                            disabled={!editableFields.rateLimiting.enabled}
                            className="text-smd"
                          />
                        </div>
                        
                        <div className="w-100px ml-8">
                          <label htmlFor="timeWindow" className="block text-sm text-gray-700 mb-1">
                            Time window (minutes)
                          </label>
                          <Input
                            type="number"
                            name="timeWindow"
                            id="timeWindow"
                            value={editableFields.rateLimiting.timeWindow}
                            onChange={handleRateLimitingChange}
                            min="1"
                            max="1440"
                            disabled={!editableFields.rateLimiting.enabled}
                            className="text-smd"
                          />
                        </div>
                      </div>
                      ) : (
                      <div>
                      </div>
                      )}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded">
                    {project.rateLimiting?.enabled ? (
                      <p>
                        <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold mr-2">
                          Enabled
                        </span>
                        Limit: {project.rateLimiting.maxMessages} messages per {Math.round(project.rateLimiting.timeWindow / 60)} minutes
                      </p>
                    ) : (
                      <p>
                        <span className="inline-block bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-semibold mr-2">
                          Disabled
                        </span>
                        No message rate limiting applied
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Time Settings */}
              <div>
                <p className="text-sm font-bold text-gray-700 mb-1">Time Settings</p>
                {isEditing && permissions.canEditProject(projectId) ? (
                  <div className="bg-gray-50 p-4 rounded space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="useCurrentTime"
                        id="useCurrentTime"
                        checked={editableFields.timeSettings?.useCurrentTime || false}
                        onChange={(e) => {
                          setEditableFields({
                            ...editableFields,
                            timeSettings: {
                              ...editableFields.timeSettings,
                              useCurrentTime: e.target.checked
                            }
                          });
                        }}
                        className="mr-2 h-4 w-4"
                      />
                      <label htmlFor="useCurrentTime" className="text-gray-700">
                        Use current date and time in messages
                      </label>
                    </div>
                    
                    {editableFields.timeSettings?.useCurrentTime && (
                      <div className="mt-2">
                        <label htmlFor="timezone" className="block text-sm text-gray-700 mb-1">
                          Timezone
                        </label>
                        <select
                          id="timezone"
                          name="timezone"
                          value={editableFields.timeSettings?.timezone || 'UTC'}
                          onChange={(e) => {
                            setEditableFields({
                              ...editableFields,
                              timeSettings: {
                                ...editableFields.timeSettings,
                                timezone: e.target.value
                              }
                            });
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md text-smd"
                        >
                          <option value="UTC">(UTC +00:00) UTC</option>
                          <option value="America/New_York">(UTC -05:00) Eastern Time - New York, Toronto</option>
                          <option value="America/Chicago">(UTC -06:00) Central Time - Chicago, Mexico City</option>
                          <option value="America/Denver">(UTC -07:00) Mountain Time - Denver, Phoenix</option>
                          <option value="America/Los_Angeles">(UTC -08:00) Pacific Time - Los Angeles, Vancouver</option>
                          <option value="America/Anchorage">(UTC -09:00) Alaska Time - Anchorage</option>
                          <option value="Pacific/Honolulu">(UTC -10:00) Hawaii Time - Honolulu</option>
                          <option value="Europe/London">(UTC +00:00) London, Dublin, Lisbon</option>
                          <option value="Europe/Paris">(UTC +01:00) Central European Time - Paris, Berlin, Rome</option>
                          <option value="Europe/Helsinki">(UTC +02:00) Eastern European Time - Helsinki, Athens, Cairo</option>
                          <option value="Europe/Moscow">(UTC +03:00) Moscow Time - Moscow, Istanbul, Riyadh</option>
                          <option value="Asia/Dubai">(UTC +04:00) Gulf Time - Dubai, Abu Dhabi</option>
                          <option value="Asia/Karachi">(UTC +05:00) Pakistan Time - Karachi, Tashkent</option>
                          <option value="Asia/Kolkata">(UTC +05:30) India Time - New Delhi, Mumbai</option>
                          <option value="Asia/Kathmandu">(UTC +05:45) Nepal Time - Kathmandu</option>
                          <option value="Asia/Dhaka">(UTC +06:00) Bangladesh Time - Dhaka, Almaty</option>
                          <option value="Asia/Yangon">(UTC +06:30) Myanmar Time - Yangon</option>
                          <option value="Asia/Bangkok">(UTC +07:00) Indochina Time - Bangkok, Hanoi, Jakarta</option>
                          <option value="Asia/Singapore">(UTC +08:00) Singapore Time - Singapore, Kuala Lumpur, Manila</option>
                          <option value="Asia/Shanghai">(UTC +08:00) China Time - Beijing, Shanghai, Hong Kong</option>
                          <option value="Asia/Tokyo">(UTC +09:00) Japan Time - Tokyo, Seoul, Pyongyang</option>
                          <option value="Australia/Adelaide">(UTC +09:30) Central Australia Time - Adelaide</option>
                          <option value="Australia/Sydney">(UTC +10:00) Eastern Australia Time - Sydney, Melbourne</option>
                          <option value="Pacific/Guam">(UTC +10:00) Chamorro Time - Guam, Port Moresby</option>
                          <option value="Pacific/Fiji">(UTC +12:00) Fiji Time - Suva</option>
                          <option value="Pacific/Auckland">(UTC +12:00) New Zealand Time - Auckland, Wellington</option>
                        </select>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded">
                    {project.timeSettings?.useCurrentTime ? (
                      <p>
                        <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold mr-2">
                          Enabled
                        </span>
                        Timezone: {project.timeSettings.timezone}
                      </p>
                    ) : (
                      <p>
                        <span className="inline-block bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-semibold mr-2">
                          Disabled
                        </span>
                        Current date and time not included in messages
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Message Quota Settings */}
              <div>
                <p className="text-sm font-bold text-gray-700 mb-1">Message Quota</p>
                {isEditing && permissions.canEditProject(projectId) ? (
                  <div className="bg-gray-50 p-4 rounded space-y-3">
                                         <div className="flex items-center">
                       <input
                         type="checkbox"
                         name="enabled"
                         id="messageQuotaEnabled"
                         checked={editableFields.messageQuota.enabled}
                         onChange={handleMessageQuotaChange}
                         className="mr-2 h-4 w-4"
                       />
                       <label htmlFor="messageQuotaEnabled" className="text-gray-700">
                         Enable message quota
                       </label>
                     </div>
                    
                    {editableFields.messageQuota.enabled ? (
                      <>
                        <div className="flex flex-wrap gap-4 mt-2">
                          <div className="w-100px">
                            <label htmlFor="totalTokens" className="block text-sm text-gray-700 mb-1">
                              Total tokens
                            </label>
                            <Input
                              type="number"
                              name="totalTokens"
                              id="totalTokens"
                              value={editableFields.messageQuota.totalTokens}
                              onChange={handleMessageQuotaChange}
                              min="1"
                              max="1000000"
                              disabled={!editableFields.messageQuota.enabled}
                              className="text-smd"
                            />
                          </div>
                          
                          <div className="w-100px ml-8">
                            <label htmlFor="refreshMonthly" className="block text-sm text-gray-700 mb-1">
                              Refresh monthly
                            </label>
                            <input
                              type="checkbox"
                              name="refreshMonthly"
                              id="refreshMonthly"
                              checked={editableFields.messageQuota.refreshMonthly}
                              onChange={handleMessageQuotaChange}
                              disabled={!editableFields.messageQuota.enabled}
                              className="mr-2 h-4 w-4"
                            />
                          </div>
                        </div>
                        
                                                 <div className="mt-2">
                           <label htmlFor="thresholdPercentage" className="block text-sm text-gray-700 mb-1">
                             Threshold percentage
                           </label>
                           <Input
                             type="number"
                             name="thresholdAlert.thresholdPercentage"
                             id="thresholdPercentage"
                             value={editableFields.messageQuota.thresholdAlert.thresholdPercentage}
                             onChange={handleMessageQuotaChange}
                             min="1"
                             max="100"
                             disabled={!editableFields.messageQuota.enabled}
                             className="text-smd"
                           />
                         </div>
                        
                        <div className="mt-2">
                          <label htmlFor="quotaExceededMessage" className="block text-sm text-gray-700 mb-1">
                            Quota exceeded message
                          </label>
                          <Textarea
                            name="quotaExceededMessage"
                            id="quotaExceededMessage"
                            value={editableFields.messageQuota.quotaExceededMessage}
                            onChange={handleMessageQuotaChange}
                            placeholder="Enter message to send when quota is exceeded"
                            rows={3}
                            disabled={!editableFields.messageQuota.enabled}
                            className="w-full text-smd"
                          />
                        </div>
                        
                        <div className="mt-2">
                          <label htmlFor="thresholdAlertEnabled" className="block text-sm text-gray-700 mb-1">
                            Enable threshold alerts
                          </label>
                          <input
                            type="checkbox"
                            name="thresholdAlert.enabled"
                            id="thresholdAlertEnabled"
                            checked={editableFields.messageQuota.thresholdAlert.enabled}
                            onChange={handleMessageQuotaChange}
                            disabled={!editableFields.messageQuota.enabled}
                            className="mr-2 h-4 w-4"
                          />
                        </div>
                        
                        {editableFields.messageQuota.thresholdAlert.enabled && (
                          <div className="mt-2">
                            <label htmlFor="emailRecipients" className="block text-sm text-gray-700 mb-1">
                              Email recipients (comma-separated)
                            </label>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {editableFields.messageQuota.thresholdAlert.emailRecipients.map((email, index) => (
                                <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                                  {email}
                                  <button
                                    type="button"
                                    onClick={() => removeEmailRecipient(email)}
                                    className="ml-1 text-blue-800 hover:text-blue-900"
                                    title="Remove recipient"
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                name="newEmailRecipient"
                                id="newEmailRecipient"
                                value={newEmailRecipient}
                                onChange={(e) => setNewEmailRecipient(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    addEmailRecipient();
                                  }
                                }}
                                placeholder="Enter email address"
                                className="flex-grow"
                              />
                              <button
                                type="button"
                                onClick={addEmailRecipient}
                                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500">Message quota is currently disabled.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded">
                    {project.messageQuota?.enabled ? (
                      <div className="flex items-center">
                        <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold mr-2">
                          Enabled
                        </span>
                        <p>
                          Quota: {formatNumber(project.messageQuota.totalTokens)} tokens
                          {project.messageQuota.refreshMonthly && (
                            <span className="ml-2 text-gray-500"> (monthly)</span>
                          )}
                        </p>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        <span className="inline-block bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-semibold mr-2">
                          Disabled
                        </span>
                        No message quota applied
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div>
                <p className="text-sm font-bold text-gray-700 mb-1">Created At</p>
                <p>{formatDate(project.createdAt)}</p>
              </div>
              
              <div>
                <p className="text-sm font-bold text-gray-700 mb-1">Last Updated</p>
                <p>{formatDate(project.updatedAt)}</p>
              </div>
              
              {isEditing && (
                <div className="pt-4">
                  <Button
                    className="btn btn-primary text-smd"
                    onClick={saveChanges}
                    disabled={updateProjectMutation.isLoading}
                  >
                    {updateProjectMutation.isLoading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiSave className="mr-2" /> Save Changes
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quota Top-Up Modal */}
        {showTopUpModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ 
              zIndex: 9999,
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowTopUpModal(false);
                setTopUpAmount('');
                setTopUpReason('');
              }
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '32rem',
                margin: '0 1rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                position: 'relative',
                zIndex: 10000
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">🚀 Top Up Message Quota</h3>
              
              <div className="mb-4">
                <label htmlFor="topUpAmount" className="block text-sm text-gray-700 mb-1">
                  Amount to Top Up (tokens)
                </label>
                <Input
                  type="number"
                  name="topUpAmount"
                  id="topUpAmount"
                  value={topUpAmount}
                  onChange={(e) => setTopUpAmount(e.target.value)}
                  min="1"
                  max="100000"
                  placeholder="Enter amount"
                  className="w-full text-smd"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="topUpReason" className="block text-sm text-gray-700 mb-1">
                  Reason for Top Up
                </label>
                <Input
                  type="text"
                  name="topUpReason"
                  id="topUpReason"
                  value={topUpReason}
                  onChange={(e) => setTopUpReason(e.target.value)}
                  placeholder="e.g., Monthly budget, Emergency increase"
                  className="w-full text-smd"
                />
              </div>

                             <div className="flex gap-3 justify-end">
                 <button
                   onClick={() => {
                     setShowTopUpModal(false);
                     setTopUpAmount('');
                     setTopUpReason('');
                   }}
                   className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                   disabled={topUpLoading}
                 >
                   Cancel
                 </button>
                 <button
                   onClick={handleTopUp}
                   className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                   disabled={!topUpAmount || isNaN(topUpAmount) || Number(topUpAmount) <= 0 || topUpLoading}
                 >
                   {topUpLoading ? (
                     <>
                       <Spinner size="sm" className="mr-2 inline" />
                       Topping Up...
                     </>
                   ) : (
                     'Confirm Top Up'
                   )}
                 </button>
               </div>
            </div>
          </div>
        )}

        {/* Quota Reset Modal */}
        {showResetModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ 
              zIndex: 9999,
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowResetModal(false);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '32rem',
                margin: '0 1rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                position: 'relative',
                zIndex: 10000
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">⚠️ Reset Message Quota</h3>
              
                             <div className="text-center mb-6">
                 <FiAlertTriangle className="text-red-500 text-4xl mb-4 mx-auto" />
                 <p className="text-sm text-gray-700 mb-4">
                   Are you sure you want to reset the message quota for this project? This will restore the quota to its maximum value.
                 </p>
                 <p className="text-sm text-gray-500">
                   Current tokens will be reset to: {formatNumber(project.messageQuota?.totalTokens || 0)} tokens
                 </p>
               </div>
 
               <div className="flex gap-3 justify-end">
                 <button
                   onClick={() => setShowResetModal(false)}
                   className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                   disabled={resetLoading}
                 >
                   Cancel
                 </button>
                 <button
                   onClick={handleQuotaReset}
                   className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                   disabled={resetLoading}
                 >
                   {resetLoading ? (
                     <>
                       <Spinner size="sm" className="mr-2 inline" />
                       Resetting...
                     </>
                   ) : (
                     'Confirm Reset'
                   )}
                 </button>
               </div>
            </div>
          </div>
        )}

        {/* Sync to Production Modal */}
        {showSyncModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ 
              zIndex: 9999,
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCancelSync();
              }
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '32rem',
                margin: '0 1rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                position: 'relative',
                zIndex: 10000
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">🚀 Sync Project to Production</h3>
              
              {syncResult ? (
                <div className="text-center">
                  <div className="text-green-600 text-2xl mb-2">✅</div>
                  <h4 className="text-lg font-medium text-green-800 mb-2">Sync Successful!</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Project:</strong> {syncResult.projectName}</p>
                    <p><strong>Client:</strong> {syncResult.clientName}</p>
                    <p><strong>Action:</strong> {syncResult.isNewProject ? 'Created new project' : 'Updated existing project'}</p>
                    
                    {syncResult.syncedConfigurations && (
                      <div className="mt-3 p-3 bg-gray-50 rounded text-xs">
                        <p className="font-medium mb-2">Synced Configurations:</p>
                        <div className="grid grid-cols-2 gap-1 text-left">
                          <div>LLM Models: {syncResult.syncedConfigurations.llmModels?.primary && syncResult.syncedConfigurations.llmModels?.secondary ? '✅' : '❌'}</div>
                          <div>Human Agents: {syncResult.syncedConfigurations.humanAgents ? '✅' : '❌'}</div>
                          <div>Rate Limiting: {syncResult.syncedConfigurations.rateLimiting ? '✅' : '❌'}</div>
                          <div>Time Settings: {syncResult.syncedConfigurations.timeSettings ? '✅' : '❌'}</div>
                          <div>WhatsApp: {syncResult.syncedConfigurations.whatsappIntegration ? '✅' : '❌'}</div>
                          <div>External APIs: {syncResult.syncedConfigurations.externalServices ? '✅' : '❌'}</div>
                        </div>
                      </div>
                    )}
                    
                    <p><strong>Synced:</strong> {new Date(syncResult.syncedAt).toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">This modal will close automatically...</p>
                </div>
              ) : (
                <>
                  <div className="text-sm text-gray-700 mb-4">
                    <p className="mb-2">You are about to sync this project to production:</p>
                    <div className="bg-gray-50 p-3 rounded border">
                      <p><strong>Name:</strong> {project.name}</p>
                      <p><strong>Client:</strong> {project.clientName}</p>
                      <p><strong>Description:</strong> {project.description || 'No description'}</p>
                      <p><strong>LLM Models:</strong> {project.primaryLLM?.model} / {project.secondaryLLM?.model}</p>
                      {project.rateLimiting?.enabled && (
                        <p><strong>Rate Limiting:</strong> {project.rateLimiting.maxMessages} messages per {Math.round(project.rateLimiting.timeWindow / 60)} minutes</p>
                      )}
                      {project.humanAgents?.enabled && (
                        <p><strong>Human Agents:</strong> Enabled</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded p-3 mb-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-orange-800">Important</h3>
                        <div className="mt-2 text-sm text-orange-700">
                          <p>This will sync the complete project configuration to production, including LLM settings, human agents configuration, rate limiting, and all other project settings.</p>
                          <p className="mt-1"><strong>Note:</strong> Sensitive data like API keys will be preserved from the existing production settings.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {syncError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      <strong>Error:</strong> {syncError}
                    </div>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={handleCancelSync}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                      disabled={syncLoading}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmSync}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      disabled={syncLoading}
                    >
                      {syncLoading ? 'Syncing...' : 'Confirm Sync'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default ProjectOverview; 